import { useRef, useState, useEffect } from "react";
import styles from "../assets/styles/timekeeper.module.css";
import Toast from "../lib/toast";
import { masterConfig } from "../configs/generalConfig";
import { useAuth } from "../lib/firebase";
import { formatData, uploadBlocker } from "../lib/data";
import { completeTimekeeperRecord } from "../lib/timekeeper";
import { submitTimekeeper, showSubmitResult } from "../lib/submit";
import { getTBAMatchVideo } from "../lib/tba";
import Backarrow from "../components/Backarrow";
import settingStyles from  "../assets/styles/settings.module.css";


// Parses the raw "auto_timestamps" text-mode input into an array of numbers, or returns null if
// it's not a valid comma-separated list of (optionally negative, optionally decimal) numbers.
// Extracted from what was previously duplicated, identically, inside the old Upload and
// Continue handlers (now submitTimestamps) — exported for direct unit testing (see TImekeeper.test.js) since this is
// the actual validation gate between scouted timestamp data and what gets persisted/uploaded.
export function parseTimestampString(raw) {
    const cleaned = String(raw ?? "").replace(/\s+/g, "");
    const valid = /^-?\d+(\.\d+)?(,-?\d+(\.\d+)?)*$/.test(cleaned);
    if (!valid) return null;
    return cleaned.split(",").map(Number);
}

// Parses the "Auto Start Time" input into a safe non-negative number, defaulting to 0 (a normal,
// non-delayed auto) for anything blank/invalid. Exported alongside parseTimestampString for the
// same reason — this is the validation gate between the raw text field and what gets uploaded.
export function parseStartTime(raw) {
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

export default function Timekeeper({ currentTab, setCurrentTab, setCurrentPage, dataMap, gamemode, eventPath, eventList, onlineStatus, scoutDataMap, setScoutDataMap, settingsYear, onSubmitted }) {
    const { user, authentication } = useAuth();
    let startScoutPage = {...masterConfig[settingsYear].startScoutPage}
    let initialPage = {...masterConfig[settingsYear].initialPage}
    const [stringMode, setStringMode] = useState(() => {
        return localStorage.getItem("stringmodeStatus") === "true";
    });
    const timeOptions = ["az", "midline"]
    const [timekeepData, setTimekeepData] = useState(new Map())
    const [inputValue, setInputValue] = useState("");
    const [startTimeInput, setStartTimeInput] = useState("0");
    const [keepType, setKeepType] = useState(() => {
        // The last mode picked (toggleType saves it); alliance zone until one is
        return localStorage.getItem("timekeepStatus") === "midline" ? "midline" : "az";
    });

    useEffect(() => {

        if (stringMode || currentTab !== "timekeep") return;

        function handleKeyDown(e) {

            if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
            }
        }

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };

    }, [stringMode, inputValue, currentTab]);

    /**
     * Submit: save locally, send to the Pi when it answers, upload to Firebase when
     * online, then one toast for the highest level reached (lib/submit.js). Timestamps
     * that don't parse are the only thing that stays on this page - saved here as typed,
     * but not queued or sent.
     */
    // A second tap while one Submit is still sending would queue the match twice.
    const submitting = useRef(false);

    async function submitTimestamps(){
        if (submitting.current) return;
        submitting.current = true;
        try {
            await submitTimestampsOnce();
        } finally {
            submitting.current = false;
        }
    }

    async function submitTimestampsOnce(){
        let uploadMap = new Map(timekeepData);
        const num = uploadMap.get("match_number");
        if (!num) {
            Toast.error("Not submitted - no match number");
            return;
        }

        if (stringMode) {
            const parsed = parseTimestampString(uploadMap.get("auto_timestamps"));
            if (parsed === null) {
                // Invalid: nothing saved or sent, just the toast (user, Oct 2026).
                Toast.error("Not submitted - invalid timestamp format. Fix the list and try again.");
                return;
            }
            uploadMap.set("auto_timestamps", parsed);
        }

        uploadMap.set("auto_start_time", parseStartTime(startTimeInput));
        // Every Timekeeper field, every time - a missing one would split the Pi's table.
        uploadMap = completeTimekeeperRecord(uploadMap, settingsYear);

        const dataObject = formatData(uploadMap);
        if (!dataObject) return; // formatData toasts why; nothing is saved or sent

        const result = await submitTimekeeper({
            record: dataObject,
            saveCopy: () => localStorage.setItem(`Timestamp_match_${num}`, JSON.stringify(Object.entries(dataObject))),
            gamemode,
            eventPath,
            uploadBlocked: uploadBlocker(user, authentication, uploadMap, eventPath, eventList, onlineStatus, settingsYear),
        });
        showSubmitResult(result);
        if (result.level === "error") return;
        // Scout.js moves on to the next robot (lib/timekeepRotation.js) and back to its start page
        if (onSubmitted) return onSubmitted();
        setCurrentTab("home")
        setCurrentPage("start")
    }

    useEffect(() => {
        if (currentTab !== "timekeep") return;

        const startScoutPage = {
            ...masterConfig[settingsYear].startScoutPage
        };

        const initialPage = {
            ...masterConfig[settingsYear].initialPage
        };

        const combinedKeys = new Set([
            ...Object.keys(startScoutPage),
            ...Object.keys(initialPage)
        ]);

        const newMap = new Map();

        combinedKeys.forEach((key) => {
            if (scoutDataMap.has(key)) {
                newMap.set(key, scoutDataMap.get(key));
            }
        });
        newMap.set("auto_timestamps", [])
        newMap.set("timekeeping_type", keepType)
        newMap.set("auto_start_time", 0)
        setTimekeepData(newMap);
        setStartTimeInput("0");

    }, [currentTab, scoutDataMap]);

    const handleSubmit = () => {
        if (inputValue.trim() === "") return;

        setTimekeepData((prev) => {
            const updated = new Map(prev);

            const oldArr = updated.get("auto_timestamps") || [];

            updated.set("auto_timestamps", [
                ...oldArr,
                Number(inputValue)
            ]);

            return updated;
        });

        setInputValue("");
    };
    function toggle(setter,getter,name) {
        setter(!getter);
        localStorage.setItem(name, String(!getter));

        setInputValue("");

        setTimekeepData((prev) => {
            const updated = new Map(prev);
            updated.set("auto_timestamps", []);
            return updated;
        });
    }

    function toggleType(setter,getter,name) {
        let thing = getter == "az" ? "midline" : "az"
        setter(thing);
        localStorage.setItem(name, String(thing));

        setInputValue("");

        setTimekeepData((prev) => {
            const updated = new Map(prev);
            updated.set("auto_timestamps", []);
            updated.set("timekeeping_type",thing)
            return updated;
        });
    }

    const handleRemoveLast = () => {

        setTimekeepData((prev) => {

            const updated = new Map(prev);

            const oldArr = updated.get("auto_timestamps") || [];

            if (oldArr.length === 0) return updated;

            updated.set(
                "auto_timestamps",
                oldArr.slice(0, -1)
            );

            return updated;
        });
    };

    const handleStringChange = (value) => {
        setInputValue(value);

        setTimekeepData((prev) => {
            const updated = new Map(prev);
            updated.set("auto_timestamps", value);
            return updated;
        });
    };

    const handleStartTimeChange = (value) => {
        setStartTimeInput(value);

        setTimekeepData((prev) => {
            const updated = new Map(prev);
            updated.set("auto_start_time", parseStartTime(value));
            return updated;
        });
    };

    const recordedTimestamps = Array.isArray(timekeepData.get("auto_timestamps"))
        ? timekeepData.get("auto_timestamps")
        : (parseTimestampString(timekeepData.get("auto_timestamps")) || []);

    const hasStringFormatError = stringMode
        && inputValue.trim() !== ""
        && parseTimestampString(inputValue) === null;

    const isStartTimeDelayed = parseStartTime(startTimeInput) > 0;

    if (currentTab == "timekeep") {
        return (
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.headerTitle}>Auto Timekeeper</div>
                    <div className={styles.headerSubtitle}>
                        Recording {keepType === "midline" ? "midline crossing" : "alliance zone crossing"} times{stringMode ? " (string mode)" : ""}
                    </div>
                </div>

                <div className={styles.startTimeBar}>
                    <span
                        className={styles.startTimeLabel}
                        title="Seconds after the match starts before this robot's auto actually began. Leave at 0 for a normal, immediate start."
                    >
                        Auto Start Time
                    </span>
                    <input
                        type="number"
                        min="0"
                        step="0.1"
                        inputMode="decimal"
                        placeholder="0"
                        value={startTimeInput}
                        onChange={(e) => handleStartTimeChange(e.target.value)}
                        className={`${styles.input} ${styles.startTimeInput}`}
                    />
                    <span className={styles.startTimeUnit}>sec</span>
                    {isStartTimeDelayed && (
                        <>
                            <span className={styles.delayBadge}>Delayed</span>
                            <button
                                type="button"
                                className={styles.resetLink}
                                onClick={() => handleStartTimeChange("0")}
                            >
                                Reset
                            </button>
                        </>
                    )}
                </div>

                <div className={styles.section}>
                    <span className={styles.sectionLabel}>Mode</span>
                    <div className={styles.modesRow}>
                        <div className={styles.modeItem}>
                            <label className={settingStyles.switch}>
                                <input type="checkbox" checked={stringMode} onChange={() => toggle(setStringMode, stringMode, "stringmodeStatus")} />
                                <span className={settingStyles.slider}></span>
                            </label>
                            <div>
                                <div className={styles.modeItemLabel}>String Mode</div>
                                <div className={styles.modeItemHelp}>Paste a comma-separated list instead of tapping times one at a time</div>
                            </div>
                        </div>

                        <div className={styles.modeItem}>
                            <label className={settingStyles.switch}>
                                <input type="checkbox" checked={keepType == "midline"} onChange={() => toggleType(setKeepType, keepType, "timekeepStatus")} />
                                <span className={settingStyles.slider}></span>
                            </label>
                            <div>
                                <div className={styles.modeItemLabel}>{keepType === "midline" ? "Midline" : "Alliance Zone"} Crossings</div>
                                <div className={styles.modeItemHelp}>Switches whether recorded times mark center-field or alliance-zone crossings</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={styles.section}>
                    <span className={styles.sectionLabel}>Record a Time</span>
                    <div className={styles.recordRow}>
                        {!stringMode ? (
                            <>
                                <input
                                    type="number"
                                    placeholder="enter time here"
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    className={styles.input}
                                />

                                <button
                                    onClick={handleSubmit}
                                    className={`${styles.button} ${styles.primaryButton}`}
                                >
                                    Enter
                                </button>

                                <button
                                    onClick={handleRemoveLast}
                                    className={styles.button}
                                    disabled={recordedTimestamps.length === 0}
                                >
                                    Remove Last
                                </button>
                            </>
                        ) : (
                            <>
                                <textarea
                                    className={styles.textarea}
                                    placeholder="Example: 1, 2, 3, 4"
                                    value={inputValue}
                                    onChange={(e) => handleStringChange(e.target.value)}
                                />
                                {hasStringFormatError && (
                                    <div className={styles.formatError}>
                                        Not a valid comma-separated list of numbers yet
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className={styles.section}>
                    <span className={styles.sectionLabel}>Recorded Times ({recordedTimestamps.length})</span>
                    <div className={styles.timesList}>
                        {recordedTimestamps.length === 0 ? (
                            <span className={styles.emptyTimes}>No times recorded yet</span>
                        ) : (
                            recordedTimestamps.map((time, index) => (
                                <span className={styles.timeChip} key={`${time}-${index}`}>
                                    <span className={styles.timeChipIndex}>{index + 1}</span>
                                    {time}
                                </span>
                            ))
                        )}
                    </div>
                </div>

                <div className={styles.spacer}></div>

                <div className={styles.actionBar}>
                    <button
                        onClick={submitTimestamps}
                        className={`${styles.button} ${styles.primaryButton}`}
                    >
                        Submit
                    </button>
                    <button className={styles.abort}
                        onClick={() => {
                            setCurrentPage("start")
                            setCurrentTab("home")}
                        }>
                            Abort
                    </button>
                </div>
            </div>
        );
    }

}
